import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, CreditCard, Printer, FileSpreadsheet, CalendarRange, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmt = (n) => `Rs.${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

const DcbReport = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [dcbData, setDcbData] = useState(null);
  const [dcbBranch, setDcbBranch] = useState('all');
  const [dcbStatus, setDcbStatus] = useState('active');
  // Mode: 'month' (single month) or 'range' (date range)
  const [mode, setMode] = useState('month');
  const [dcbMonth, setDcbMonth] = useState(new Date().getMonth() + 1);
  const [dcbYear, setDcbYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const fetchDCBReport = async () => {
    setLoading(true);
    try {
      const params = { loan_status: dcbStatus };
      if (mode === 'range') {
        if (!startDate || !endDate) { toast.error('Pick a start and end date'); setLoading(false); return; }
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.month = dcbMonth;
        params.year = dcbYear;
      }
      if (dcbBranch !== 'all') params.branch_id = dcbBranch;
      const resp = await api.get('/reports/dcb', { params });
      setDcbData(resp.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to fetch DCB report'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => (dcbBranch === 'all' ? 'All Branches' : branches.find((b) => b.id === dcbBranch)?.name || 'Unknown');
  const getPeriodLabel = () => (
    mode === 'range' && dcbData?.period_start
      ? `${dcbData.period_start} → ${dcbData.period_end}`
      : `${MONTHS[dcbMonth - 1]} ${dcbYear}`
  );

  const handleExcel = () => {
    if (!dcbData || !(dcbData.entries || []).length) { toast.error('Generate the report first'); return; }
    const sm = dcbData.summary || {};
    const rows = (dcbData.entries || []).map((l, i) => ({
      'SN': l.sn || (i + 1),
      'SHG Name': l.shg_name || '-',
      'VO Name': l.vo_name || '-',
      'CLF Name': l.clf_name || '-',
      'Entrepreneur': l.entrepreneur_name || '-',
      'Loan No.': l.loan_number || '-',
      'EMIs Before': l.emis_before_period || 0,
      'EMIs In Period': l.emis_in_period || 0,
      'Opening Arrears (P)': Math.round(l.opening_arrears_p || 0),
      'Opening Arrears (I)': Math.round(l.opening_arrears_i || 0),
      'Current Demand (P)': Math.round(l.current_demand_p || 0),
      'Current Demand (I)': Math.round(l.current_demand_i || 0),
      'Total Demand': Math.round(l.total_demand || 0),
      'Arrear Collection': Math.round(l.arrear_collection || 0),
      'Current Collection': Math.round(l.current_collection || 0),
      'Total Collection': Math.round(l.total_collection || 0),
      'Closing Balance': Math.round(l.closing_balance || 0),
      'Balance %': l.balance_pct || 0,
      'Overdue Days': l.overdue_days || 0,
    }));
    exportToExcel({
      filename: `WWAGS_DCB_${getPeriodLabel().replace(/[^A-Za-z0-9]+/g, '_')}_${getBranchName().replace(/\s+/g, '_')}`,
      sheetName: `DCB ${getPeriodLabel()}`,
      rows,
      kpis: [
        { label: 'Branch', value: getBranchName() },
        { label: 'Period', value: getPeriodLabel() },
        { label: 'Total Loans', value: dcbData.total_loans || 0 },
        { label: 'Opening Arrears', value: Math.round(sm.opening_arrears || 0) },
        { label: 'Current Demand', value: Math.round(sm.current_demand || 0) },
        { label: 'Total Demand', value: Math.round(sm.total_demand || 0) },
        { label: 'Total Collection', value: Math.round(sm.total_collection || 0) },
        { label: 'Closing Balance', value: Math.round(sm.closing_balance || 0) },
        { label: 'Balance %', value: sm.balance_pct || 0 },
      ],
    });
  };

  const handlePrint = () => {
    if (!dcbData) return;
    const sm = dcbData.summary || {};
    const entries = dcbData.entries || [];
    const rows = entries.map((l, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f9f9f9'}">
        <td style="text-align:center">${l.sn || i + 1}</td>
        <td>${l.shg_name || '-'}</td>
        <td>${l.vo_name || '-'}</td>
        <td>${l.clf_name || '-'}</td>
        <td>${l.entrepreneur_name || '-'}</td>
        <td>${l.loan_number || '-'}</td>
        <td style="text-align:right">${fmt(l.opening_arrears)}</td>
        <td style="text-align:right">${fmt(l.current_demand)}</td>
        <td style="text-align:right"><b>${fmt(l.total_demand)}</b></td>
        <td style="text-align:right; color:#059669">${fmt(l.arrear_collection)}</td>
        <td style="text-align:right; color:#059669">${fmt(l.current_collection)}</td>
        <td style="text-align:right; color:#059669"><b>${fmt(l.total_collection)}</b></td>
        <td style="text-align:right; color:#b91c1c"><b>${fmt(l.closing_balance)}</b></td>
        <td style="text-align:center">${(l.balance_pct || 0).toFixed(1)}%</td>
        <td style="text-align:center">${l.overdue_days || 0}</td>
      </tr>
    `).join('');
    const body = `
      <div class="cards">
        <div class="card"><small>Period</small><b>${getPeriodLabel()}</b></div>
        <div class="card"><small>Opening Arrears</small><b>${fmt(sm.opening_arrears)}</b></div>
        <div class="card"><small>Current Demand</small><b>${fmt(sm.current_demand)}</b></div>
        <div class="card"><small>Total Demand</small><b>${fmt(sm.total_demand)}</b></div>
        <div class="card"><small>Total Collection</small><b>${fmt(sm.total_collection)}</b></div>
        <div class="card"><small>Closing Balance</small><b>${fmt(sm.closing_balance)}</b></div>
        <div class="card"><small>Balance %</small><b>${(sm.balance_pct || 0).toFixed(1)}%</b></div>
      </div>
      <table>
        <thead><tr>
          <th>SN</th><th>SHG</th><th>VO</th><th>CLF</th><th>Entrepreneur</th><th>Loan No.</th>
          <th>Opening Arrears</th><th>Current Demand</th><th>Total Demand</th>
          <th>Arrear Coll.</th><th>Current Coll.</th><th>Total Coll.</th>
          <th>Closing Bal.</th><th>Bal %</th><th>O/D Days</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `;
    openPrintWindow(buildPrintHTML(`DCB Report — ${getPeriodLabel()}`, body, getBranchName()));
  };

  const sm = dcbData?.summary || {};

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="dcb-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CreditCard className="w-6 h-6 text-rose-700" /> Demand-Collection-Balance (DCB)</h1>
          <p className="text-sm text-slate-600">
            <b>Total Demand</b> = Opening Arrears + Current Demand • <b>Closing Balance</b> = Total Demand − Total Collection
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 shadow-sm">
        {/* Mode toggle */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-slate-500 uppercase font-semibold tracking-wide">Period mode</span>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setMode('month')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${mode === 'month' ? 'bg-rose-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="mode-month"
            >
              <Calendar className="w-3.5 h-3.5" /> Single Month
            </button>
            <button
              type="button"
              onClick={() => setMode('range')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${mode === 'range' ? 'bg-rose-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="mode-range"
            >
              <CalendarRange className="w-3.5 h-3.5" /> Date Range
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          {mode === 'month' ? (
            <>
              <div>
                <Label className="text-xs">Month</Label>
                <Select value={dcbMonth.toString()} onValueChange={(v) => setDcbMonth(parseInt(v))}>
                  <SelectTrigger className="w-36" data-testid="dcb-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Select value={dcbYear.toString()} onValueChange={(v) => setDcbYear(parseInt(v))}>
                  <SelectTrigger className="w-28" data-testid="dcb-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2023, 2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" data-testid="dcb-start-date" />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" data-testid="dcb-end-date" />
              </div>
            </>
          )}

          {isAdmin() && (
            <div>
              <Label className="text-xs">Branch</Label>
              <Select value={dcbBranch} onValueChange={setDcbBranch}>
                <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Loan Status</Label>
            <Select value={dcbStatus} onValueChange={setDcbStatus}>
              <SelectTrigger className="w-36" data-testid="dcb-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={fetchDCBReport} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="generate-dcb">Generate</Button>
          {dcbData && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
          {dcbData && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
        </div>
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-700"></div></div>}

      {dcbData && !loading && (
        <div>
          {/* Summary Cards — DCB flow */}
          <div className="mb-2 text-xs text-slate-500 italic">
            Period: <b className="text-slate-700">{dcbData.period_start} → {dcbData.period_end}</b> • {dcbData.total_loans} loan(s)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase font-semibold">Opening Arrears</p>
              <p className="text-base font-bold text-slate-800 mt-1" data-testid="s-opening">{fmt(sm.opening_arrears)}</p>
              <p className="text-[10px] text-slate-400 mt-1">P: {fmt(sm.opening_arrears_p)} • I: {fmt(sm.opening_arrears_i)}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-[10px] text-blue-600 uppercase font-semibold">+ Current Demand</p>
              <p className="text-base font-bold text-blue-800 mt-1" data-testid="s-current-demand">{fmt(sm.current_demand)}</p>
              <p className="text-[10px] text-blue-500 mt-1">P: {fmt(sm.current_demand_p)} • I: {fmt(sm.current_demand_i)}</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-[10px] text-indigo-600 uppercase font-semibold">= Total Demand</p>
              <p className="text-base font-bold text-indigo-800 mt-1" data-testid="s-total-demand">{fmt(sm.total_demand)}</p>
              <p className="text-[10px] text-indigo-500 mt-1">P: {fmt(sm.total_demand_p)} • I: {fmt(sm.total_demand_i)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-[10px] text-emerald-600 uppercase font-semibold">− Total Collection</p>
              <p className="text-base font-bold text-emerald-800 mt-1" data-testid="s-total-collection">{fmt(sm.total_collection)}</p>
              <p className="text-[10px] text-emerald-500 mt-1">Arrear: {fmt(sm.arrear_collection)} • Current: {fmt(sm.current_collection)}</p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <p className="text-[10px] text-rose-600 uppercase font-semibold">= Closing Balance</p>
              <p className="text-base font-bold text-rose-800 mt-1" data-testid="s-closing">{fmt(sm.closing_balance)}</p>
              <p className="text-[10px] text-rose-500 mt-1">P: {fmt(sm.closing_balance_p)} • I: {fmt(sm.closing_balance_i)}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-[10px] text-amber-600 uppercase font-semibold">Balance %</p>
              <p className="text-base font-bold text-amber-800 mt-1" data-testid="s-balance-pct">{(sm.balance_pct || 0).toFixed(1)}%</p>
              <p className="text-[10px] text-amber-500 mt-1">Outstanding / Total Demand</p>
            </div>
          </div>

          {/* Detailed table */}
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200 shadow-sm">
            <table className="w-full text-xs">
              <thead className="bg-rose-700 text-white sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-center">SN</th>
                  <th className="px-2 py-2 text-left">SHG</th>
                  <th className="px-2 py-2 text-left">VO</th>
                  <th className="px-2 py-2 text-left">CLF</th>
                  <th className="px-2 py-2 text-left">Entrepreneur</th>
                  <th className="px-2 py-2 text-left">Loan No.</th>
                  <th className="px-2 py-2 text-right">Opening Arrears</th>
                  <th className="px-2 py-2 text-right">Current Demand</th>
                  <th className="px-2 py-2 text-right bg-indigo-800">Total Demand</th>
                  <th className="px-2 py-2 text-right">Arrear Coll.</th>
                  <th className="px-2 py-2 text-right">Current Coll.</th>
                  <th className="px-2 py-2 text-right bg-emerald-800">Total Coll.</th>
                  <th className="px-2 py-2 text-right bg-rose-900">Closing Bal.</th>
                  <th className="px-2 py-2 text-center">Bal %</th>
                  <th className="px-2 py-2 text-center">O/D Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(dcbData.entries || []).length === 0 ? (
                  <tr><td colSpan={15} className="px-4 py-8 text-center text-slate-500">No entries found</td></tr>
                ) : (
                  (dcbData.entries || []).map((entry, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? '' : 'bg-slate-50'} hover:bg-rose-50/40`} data-testid={`dcb-row-${idx}`}>
                      <td className="px-2 py-2 text-center">{entry.sn}</td>
                      <td className="px-2 py-2">{entry.shg_name}</td>
                      <td className="px-2 py-2">{entry.vo_name || '-'}</td>
                      <td className="px-2 py-2">{entry.clf_name || '-'}</td>
                      <td className="px-2 py-2">{entry.entrepreneur_name}</td>
                      <td className="px-2 py-2 font-medium">{entry.loan_number}</td>
                      <td className="px-2 py-2 text-right">{fmt(entry.opening_arrears)}</td>
                      <td className="px-2 py-2 text-right text-blue-700">{fmt(entry.current_demand)}</td>
                      <td className="px-2 py-2 text-right font-semibold text-indigo-700 bg-indigo-50/40">{fmt(entry.total_demand)}</td>
                      <td className="px-2 py-2 text-right text-emerald-700">{fmt(entry.arrear_collection)}</td>
                      <td className="px-2 py-2 text-right text-emerald-700">{fmt(entry.current_collection)}</td>
                      <td className="px-2 py-2 text-right font-semibold text-emerald-800 bg-emerald-50/40">{fmt(entry.total_collection)}</td>
                      <td className={`px-2 py-2 text-right font-bold ${entry.closing_balance > 0 ? 'text-rose-700 bg-rose-50/40' : 'text-emerald-700 bg-emerald-50/40'}`}>{fmt(entry.closing_balance)}</td>
                      <td className={`px-2 py-2 text-center font-medium ${entry.balance_pct >= 50 ? 'text-red-600' : entry.balance_pct > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{(entry.balance_pct || 0).toFixed(1)}%</td>
                      <td className={`px-2 py-2 text-center ${entry.overdue_days > 30 ? 'text-red-600 font-bold' : entry.overdue_days > 0 ? 'text-amber-600' : ''}`}>{entry.overdue_days || 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {(dcbData.entries || []).length > 0 && (
                <tfoot className="bg-slate-100 font-semibold text-slate-800 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={6} className="px-2 py-2 text-right">TOTALS →</td>
                    <td className="px-2 py-2 text-right">{fmt(sm.opening_arrears)}</td>
                    <td className="px-2 py-2 text-right">{fmt(sm.current_demand)}</td>
                    <td className="px-2 py-2 text-right text-indigo-800">{fmt(sm.total_demand)}</td>
                    <td className="px-2 py-2 text-right text-emerald-700">{fmt(sm.arrear_collection)}</td>
                    <td className="px-2 py-2 text-right text-emerald-700">{fmt(sm.current_collection)}</td>
                    <td className="px-2 py-2 text-right text-emerald-800">{fmt(sm.total_collection)}</td>
                    <td className="px-2 py-2 text-right text-rose-800">{fmt(sm.closing_balance)}</td>
                    <td className="px-2 py-2 text-center">{(sm.balance_pct || 0).toFixed(1)}%</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {!dcbData && !loading && (
        <div className="text-center py-12 text-slate-500">
          <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Select a period and click <b>Generate</b> to view the DCB report.</p>
          <p className="text-xs mt-1 text-slate-400">Switch to <b>Date Range</b> mode to view multiple months at once.</p>
        </div>
      )}
    </div>
  );
};

export default DcbReport;
